import { falCakeCandles } from 'shared/@generated/icons/falCakeCandles';
import { falClock } from 'shared/@generated/icons/falClock';
import { fasBolt } from 'shared/@generated/icons/fasBolt';
import { useStaticRoute } from 'shared/library/routes/route';
import { Platform, View } from 'react-native';
import { Props, Listing } from 'shared/@types';
import { MediaAsset, StatType } from 'shared/@generated/models';
import { Icon } from 'shared/components/icon';
import { Image } from 'shared/components/image';
import { Gallery } from 'shared/components/gallery';
import { Link } from 'shared/components/link';
import { Text } from 'shared/components/text';
import UserCard from 'shared/components/user-card';
import LikeButton from 'shared/components/like-button';
import { useFormatter } from 'shared/library/use-formatter';
import { useStatsBatcher } from 'shared/library/stats-batcher';
import { useInViewOnce } from 'shared/library/use-in-view';
import { useCallback, memo, useEffect, useRef } from 'react';
import { setListingPreview } from 'shared/state/listing-preview';
import { prefetchListingDetailImage } from 'shared/library/prefetch-listing-detail-image';
import { useRouter } from 'shared/library/use-router';
import { useTranslation } from 'react-i18next';
import { ListingFeatureList } from 'shared/components/listing-feature-list';
import {
  getListingBreedTypeLabelKey,
  getPurebredBadgeLabelKey,
} from 'shared/library/listing-breed-type';
import { hasListingVerifiedPedigree } from 'shared/library/listing-pedigree';
import { listingFeatureIcons } from 'shared/components/listing-feature-icons';
import { BreedName } from 'shared/components/breed-name';

const ListingCard = memo(function ListingCard({
  listing,
  boosted,
  priorityImage,
}: Props<{ listing: Listing; boosted?: boolean; priorityImage?: boolean }>) {
  const { formatCurrency, formatDateAsTimeAgo, formatDateAsAge } = useFormatter();
  const { t } = useTranslation('components');
  const { batchIncrementStats } = useStatsBatcher();
  const route = useStaticRoute();
  const router = useRouter();
  const mediaAssets =
    listing.media_assets && listing.media_assets.length > 0
      ? listing.media_assets
      : listing.avatar
        ? [listing.avatar]
        : [];
  const mediaAsset = mediaAssets[0];
  const listingHref = route('listings.show', { listing: listing.slug });
  const canOpenListing = Boolean(listing.published_at);
  const isDeleted = Boolean(listing.deleted_at);
  const selectedMediaAssetRef = useRef<MediaAsset | null | undefined>(mediaAsset);
  const description =
    listing.short_description?.replace(/[\r\n]+/g, ' ') ??
    listing.description?.slice(0, 100).replace(/[\r\n]+/g, ' ');
  const descriptionLineFill = '\n\u00A0\n\u00A0';
  const breedTypeLabelKey = getListingBreedTypeLabelKey(listing);
  const breedTypeLabel = t(`listing_breed_type_badge.${breedTypeLabelKey}`);
  const hasPedigree = hasListingVerifiedPedigree(listing);
  const breedTypeTooltip =
    breedTypeLabelKey === 'pedigree_dog' || breedTypeLabelKey === 'pedigree_cat'
      ? t(`listing_breed_type_badge.${getPurebredBadgeLabelKey(listing.animal, hasPedigree)}`)
      : t(
          `screens:listings.show_listing.${
            breedTypeLabelKey === 'crossbreed'
              ? 'crossbreed_description'
              : 'mixed_breed_description'
          }`,
        );
  const listingWithGenderCounts = listing as Listing & {
    males_count?: number;
    females_count?: number;
  };
  const malesCount = listingWithGenderCounts.males_count ?? 0;
  const femalesCount = listingWithGenderCounts.females_count ?? 0;
  const hasMales = Boolean(listing.animal) && malesCount > 0;
  const hasFemales = Boolean(listing.animal) && femalesCount > 0;

  useEffect(() => {
    selectedMediaAssetRef.current = mediaAsset;
  }, [mediaAsset]);

  const logMediaAssetStats = useCallback(
    (asset: MediaAsset | null | undefined, statType: StatType) => {
      if (!asset?.uuid) return;

      batchIncrementStats(asset.uuid, statType);
    },
    [batchIncrementStats],
  );

  const ref = useInViewOnce(() => {
    logMediaAssetStats(mediaAsset, StatType.Impression);
  });

  const prepareListingOpen = useCallback(
    (clickedMediaAsset?: MediaAsset | null) => {
      const statsMediaAsset = clickedMediaAsset ?? selectedMediaAssetRef.current ?? mediaAsset;

      setListingPreview(listing);
      prefetchListingDetailImage(listing);
      logMediaAssetStats(statsMediaAsset, StatType.Click);
    },
    [listing, logMediaAssetStats, mediaAsset],
  );

  const handleSelectedMediaChange = useCallback((selectedMediaAsset: MediaAsset) => {
    selectedMediaAssetRef.current = selectedMediaAsset;
  }, []);

  const openListingFromMedia = useCallback(
    (clickedMediaAsset: MediaAsset) => {
      prepareListingOpen(clickedMediaAsset);
      void router.push(listingHref);
    },
    [listingHref, prepareListingOpen, router],
  );

  return (
    <View
      ref={ref}
      className={
        Platform.OS === 'web' ? 'relative h-full flex-col hover:z-20' : 'relative flex-col'
      }
      testID={`listing-card-${listing.slug}`}
    >
      {!isDeleted && (
        <View className={'absolute left-0 top-0 z-10 p-2'}>
          <LikeButton
            modelId={listing.id}
            modelType={'App\\Models\\Listing'}
            testID={`listing-card-like-${listing.slug}`}
          />
        </View>
      )}

      {boosted && !isDeleted && (
        <View
          className={
            'absolute right-0 top-0 z-10 m-3 flex-row items-center gap-1 rounded-full bg-yellow-400 px-1.5 py-1'
          }
        >
          <Icon icon={fasBolt} className={'size-2.5 text-yellow-950'} />
          <Text className={'text-2xs font-black text-yellow-950'}>Boost</Text>
        </View>
      )}

      {isDeleted ? (
        <Link
          href={listingHref}
          asBlock
          disabled={!canOpenListing}
          onPress={() => prepareListingOpen()}
        >
          <View className={'aspect-4/3 w-full overflow-hidden rounded-t'}>
            <View
              className={'absolute inset-0 z-10 items-center justify-center bg-black/30 px-3'}
              testID={'listing-card-deleted'}
              pointerEvents={'none'}
            >
              <Text className={'text-center text-lg font-bold text-white'}>
                {t('screens:listings.show_listing.deleted_listing')}
              </Text>
            </View>

            <Image
              src={mediaAsset}
              alt={listing.breed_name}
              containerClassName={'size-full'}
              imageClassName={'scale-110 object-cover blur-lg'}
              sizeType={'card'}
              priority={priorityImage === true}
            />
          </View>
        </Link>
      ) : mediaAssets.length > 1 ? (
        <Gallery
          items={mediaAssets}
          defaultAlt={listing.title}
          variant={'card'}
          enableFullscreen={false}
          showCounter={false}
          showThumbnails={false}
          arrowSize={'compact'}
          priority={priorityImage === true}
          onSelectedMediaChange={handleSelectedMediaChange}
          onMediaPress={canOpenListing ? openListingFromMedia : undefined}
        />
      ) : (
        <Link
          href={listingHref}
          asBlock
          disabled={!canOpenListing}
          onPress={() => prepareListingOpen()}
        >
          <Image
            src={mediaAsset}
            alt={listing.title}
            containerClassName={'aspect-4/3 w-full group-hover:opacity-75'}
            imageClassName={'rounded-t object-cover'}
            sizeType={'card'}
            priority={priorityImage === true}
          />
        </Link>
      )}

      <Link
        containerClassName={Platform.OS === 'web' ? 'flex-1 bg-white' : 'bg-white'}
        href={listingHref}
        asBlock
        disabled={!canOpenListing}
        onPress={() => prepareListingOpen()}
      >
        <View
          className={
            Platform.OS === 'web'
              ? 'border-brand-brown-dark/15 flex-1 rounded-b border border-t-0 p-3'
              : 'border-brand-brown-dark/15 rounded-b border border-t-0 p-3'
          }
        >
          <View className={'flex-row items-center gap-1.5'}>
            <Icon icon={falCakeCandles} className={'size-3 text-stone-500'} />
            <Text className={'flex-1 text-xs text-stone-500'}>
              {formatDateAsAge(listing.birth_date!)}
            </Text>

            {listing.published_at && (
              <>
                <Text className={'text-xs text-stone-500'}>
                  {formatDateAsTimeAgo(listing.published_at)}
                </Text>
                <Icon icon={falClock} className={'size-3 text-stone-500'} />
              </>
            )}
          </View>

          <View className={'mt-3 flex-row items-center gap-1.5'}>
            <Text className={'min-w-0 shrink text-lg font-bold group-hover:underline'}>
              <BreedName
                name={listing.breed_name}
                animal={listing.animal}
                breedType={listing.breed_type}
                hasVerifiedPedigree={hasPedigree}
              />
            </Text>
          </View>
          <Text className={'text-xl font-black text-green-600 group-hover:underline'}>
            {formatCurrency(listing.price!, listing.currency!)}
          </Text>

          <Text className={'mt-3 text-sm text-stone-700'} numberOfLines={3}>
            {description ? `${description}${descriptionLineFill}` : `\u00A0${descriptionLineFill}`}
          </Text>

          <ListingFeatureList
            className={Platform.OS === 'web' ? 'mt-3 flex-1' : 'mt-3'}
            items={[
              ...(hasMales
                ? [
                    {
                      icon: listingFeatureIcons.male,
                      title: t(`listing_feature_list.${listing.animal}_male`, {
                        count: malesCount,
                      }),
                    },
                  ]
                : []),
              ...(hasFemales
                ? [
                    {
                      icon: listingFeatureIcons.female,
                      title: t(`listing_feature_list.${listing.animal}_female`, {
                        count: femalesCount,
                      }),
                    },
                  ]
                : []),
              {
                icon: listingFeatureIcons.breed,
                title: breedTypeLabel,
                tooltip: breedTypeTooltip,
              },
              ...(hasPedigree
                ? [
                    {
                      icon: listingFeatureIcons.pedigree,
                      title: t('listing_feature_list.pedigree'),
                      tooltip: t('listing_feature_list.pedigree_tooltip'),
                    },
                  ]
                : []),
              ...(listing.is_insured
                ? [
                    {
                      icon: listingFeatureIcons.insurance,
                      title: t('litter_insurance_badge.litter_insurance'),
                      tooltip: t('listing_feature_list.litter_insurance_tooltip'),
                    },
                  ]
                : []),
            ]}
          />

          <View className={'mt-3'}>
            <UserCard user={listing.user} small={true} />
          </View>
        </View>
      </Link>
    </View>
  );
});

export { ListingCard };
